import type {
  CommercialInvoiceAnalysisResult,
  CommercialInvoiceDeepCheckLineItem,
} from "../../../features/commercialInvoiceAnalysis/types";
import type { BookingState } from "../../../contexts/BookingWizardContext";
import { extractSupplierContact } from "./extractSupplierContact";
import { normalizeSupplierEmail } from "./smartDetailsFormRules";

interface LookupItem {
  id: number;
  name: string;
}

export interface SupplierDraft {
  name: string;
  email: string;
  phone: string;
  wechat?: string;
  country: string;
  province: string;
  city: string;
  address: string;
}

export interface MappedBookingDraft {
  transport: {
    transport_type?: number;
    booking_type?: number | null;
    booking_incoterm?: number;
    origin_pickup_date?: string;
  };
  supplier: SupplierDraft;
  products: NonNullable<BookingState["products"]>;
  reference?: string;
  missingFields: string[];
}

export const coerceToString = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  const trimmed = String(value).trim();
  return trimmed || null;
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const str = coerceToString(value);
  if (!str) return null;
  const cleaned = str.replace(/[^0-9.,-]/g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : null;
};

const parseQuantity = (value: unknown): number => {
  const num = parseNumber(value);
  if (num == null || num < 1) return 1;
  return Math.round(num);
};

const matchIncotermId = (
  raw: unknown,
  incoterms: LookupItem[]
): number | undefined => {
  const str = coerceToString(raw);
  if (!str) return undefined;
  const upper = str.toUpperCase();
  const exact = incoterms.find((i) => upper.includes(i.name.toUpperCase()));
  return exact?.id;
};

const splitAddress = (
  address: unknown
): { country: string; city: string; province: string } => {
  const str = coerceToString(address);
  if (!str) {
    return { country: "", city: "", province: "" };
  }

  const parts = str
    .split(/[,;\n]/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length >= 3) {
    return {
      country: parts[parts.length - 1] ?? "",
      province: parts[parts.length - 2] ?? "",
      city: parts[parts.length - 3] ?? "",
    };
  }

  if (parts.length === 2) {
    return { city: parts[0] ?? "", province: "", country: parts[1] ?? "" };
  }

  return { city: parts[0] ?? "", province: "", country: "" };
};

const mapLineItemToProduct = (
  item: CommercialInvoiceDeepCheckLineItem,
  index: number,
  totalWeight: number | null,
  itemCount: number
): NonNullable<BookingState["products"]>[number] => {
  const perItemWeight =
    totalWeight && itemCount > 0
      ? Math.round((totalWeight / itemCount) * 100) / 100
      : null;

  return {
    name: coerceToString(item.description) || `Product ${index + 1}`,
    hs_code: coerceToString(item.hsCode) || null,
    gross_weight: perItemWeight,
    cbm: null,
    value: parseNumber(item.total) ?? parseNumber(item.unitPrice),
    pieces: parseQuantity(item.quantity),
    cartons: 1,
    ean: null,
    isNew: true,
  };
};

export const mapAnalysisToBooking = (
  result: CommercialInvoiceAnalysisResult,
  incoterms: LookupItem[],
  transportTypes: LookupItem[]
): MappedBookingDraft => {
  const missingFields: string[] = [];
  const extracted = result.deepCheck?.extracted;
  const lineItems = result.deepCheck?.lineItems ?? [];

  const booking_incoterm = matchIncotermId(extracted?.incoterm, incoterms);
  if (!booking_incoterm) missingFields.push("transport.booking_incoterm");

  const seafreight = transportTypes.find(
    (t) =>
      t.name.toLowerCase().includes("sea") ||
      t.name.toLowerCase().includes("ocean")
  );
  const transport_type = seafreight?.id ?? transportTypes[0]?.id;
  if (!transport_type) missingFields.push("transport.transport_type");

  const addressParts = splitAddress(extracted?.sellerAddress);
  const contact = extractSupplierContact({
    invoiceText: result.extractedTextPreview,
    sellerName: extracted?.sellerName,
    sellerEmail: extracted?.sellerEmail,
    sellerPhone: extracted?.sellerPhone,
    sellerWechat: extracted?.sellerWechat,
  });

  const supplier: SupplierDraft = {
    name: coerceToString(extracted?.sellerName) || "",
    email: normalizeSupplierEmail(contact.email || ""),
    phone: contact.phone || "",
    wechat: contact.wechat || undefined,
    country: addressParts.country,
    province: addressParts.province,
    city: addressParts.city,
    address: coerceToString(extracted?.sellerAddress) || "",
  };

  if (!supplier.name) missingFields.push("supplier.name");
  if (!supplier.email) missingFields.push("supplier.email");
  if (!supplier.phone) missingFields.push("supplier.phone");
  if (!supplier.country) missingFields.push("supplier.country");
  if (!supplier.city) missingFields.push("supplier.city");
  if (!supplier.address) missingFields.push("supplier.address");

  const totalWeight = parseNumber(extracted?.grossWeight);
  const products =
    lineItems.length > 0
      ? lineItems.map((item, index) =>
          mapLineItemToProduct(item, index, totalWeight, lineItems.length)
        )
      : [];

  if (products.length === 0) missingFields.push("products");

  products.forEach((product, index) => {
    if (!product.hs_code) missingFields.push(`products.${index}.hs_code`);
    if (product.gross_weight == null)
      missingFields.push(`products.${index}.gross_weight`);
    if (product.cbm == null) missingFields.push(`products.${index}.cbm`);
    if (product.value == null) missingFields.push(`products.${index}.value`);
  });

  if (!extracted?.incoterm && result.requiredFields) {
    const incotermField = result.requiredFields.find((f) => f.id === "incoterms");
    if (incotermField && !incotermField.present) {
      missingFields.push("transport.booking_incoterm");
    }
  }

  // Never extracted from a commercial invoice — always user input
  missingFields.push("transport.origin_pickup_date");
  if (transport_type === 2) {
    missingFields.push("transport.booking_type");
  }

  return {
    transport: {
      transport_type,
      booking_type: transport_type === 2 ? undefined : null,
      booking_incoterm,
      origin_pickup_date: undefined,
    },
    supplier,
    products,
    reference: coerceToString(extracted?.invoiceNumber) || undefined,
    missingFields: [...new Set(missingFields)],
  };
};

export const isMissingField = (
  missingFields: string[],
  fieldKey: string
): boolean => missingFields.includes(fieldKey);

export interface MissingFieldInput {
  transport_type?: number;
  booking_type?: number | null;
  service_level?: string | null;
  booking_incoterm?: number;
  origin_pickup_date?: unknown;
  supplier_name?: string;
  supplier_email?: string;
  supplier_phone?: string;
  supplier_country?: string;
  supplier_province?: string;
  supplier_city?: string;
  supplier_address?: string;
  products?: NonNullable<BookingState["products"]>;
}

/** Shared quest/unlocked split — keeps left/right columns in sync with form values. */
export const computeMissingBookingFields = (
  values: MissingFieldInput
): string[] => {
  const next: string[] = [];
  const products = values.products ?? [];

  if (!values.transport_type) next.push("transport.transport_type");
  if (values.transport_type === 2 && !values.booking_type) {
    next.push("transport.booking_type");
  }
  if (
    values.transport_type === 2 &&
    values.booking_type === 2 &&
    !values.service_level
  ) {
    next.push("transport.service_level");
  }
  if (!values.booking_incoterm) next.push("transport.booking_incoterm");
  if (!values.origin_pickup_date) next.push("transport.origin_pickup_date");
  if (!values.supplier_name) next.push("supplier.name");
  if (!values.supplier_email) next.push("supplier.email");
  if (!values.supplier_phone) next.push("supplier.phone");
  if (!values.supplier_country) next.push("supplier.country");
  if (!values.supplier_province) next.push("supplier.province");
  if (!values.supplier_city) next.push("supplier.city");
  if (!values.supplier_address) next.push("supplier.address");
  if (products.length === 0) next.push("products");

  products.forEach((product, index) => {
    if (!product.hs_code) next.push(`products.${index}.hs_code`);
    if (product.gross_weight == null) next.push(`products.${index}.gross_weight`);
    if (product.cbm == null) next.push(`products.${index}.cbm`);
    if (product.value == null) next.push(`products.${index}.value`);
  });

  return next;
};
